import React from "react";
import { useContent } from "../lib/content";
import { isHtmlString, normalizeRichTextHtml } from "../lib/sanitize";
import { controlledTypeStyle } from "../lib/typography";

const FALLBACK_STATS = [
  { k: "Projects rendered", v: "120+" },
  { k: "Countries delivered", v: "18" },
  { k: "Years in practice", v: "09" },
  { k: "Quiet hours daily", v: "04" },
];

function fontSize(value, fallback, min, max) {
  const n = Number(value);
  const safe = Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : fallback;
  return `${safe}px`;
}

export default function StudioStrip() {
  const { content } = useContent();
  const eyebrow = content?.studio_eyebrow ?? "⸺ The Studio";
  const headingPre = content?.studio_heading_pre ?? "A practice of ";
  const headingItalic = content?.studio_heading_italic ?? "patience";
  const headingPost =
    content?.studio_heading_post ?? ", assembled one frame at a time.";
  const para1 = content?.studio_para1 ?? "";
  const para2 = content?.studio_para2 ?? "";
  const stats = content?.studio_stats ?? FALLBACK_STATS;
  const leftHeadingSize = fontSize(content?.typography_studio_heading_size, 48, 20, 72);

  return (
    <section
      id="studio"
      data-testid="studio-strip"
      className="scroll-effect-section relative border-y border-bone-50/5 py-20 md:py-28 bg-ink-800/40"
    >
      <div className="container grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-bone-200/50 mb-4">
            {eyebrow}
          </div>
          <h2
            className="font-display text-4xl md:text-5xl tracking-tightest leading-[0.95] text-balance"
            style={controlledTypeStyle({
              fontSize: leftHeadingSize,
              font: content?.typography_studio_heading_font,
            })}
          >
            {headingPre}
            <span className="italic text-bone-200/70">{headingItalic}</span>
            {headingPost}
          </h2>
        </div>
        <div className="md:col-span-6 md:col-start-7 space-y-6 text-bone-200/70 leading-relaxed">
          {para1 &&
            (isHtmlString(para1) ? (
              <div
                className="prose-rich"
                dangerouslySetInnerHTML={{ __html: normalizeRichTextHtml(para1) }}
              />
            ) : (
              <p>{para1}</p>
            ))}
          {para2 &&
            (isHtmlString(para2) ? (
              <div
                className="prose-rich"
                dangerouslySetInnerHTML={{ __html: normalizeRichTextHtml(para2) }}
              />
            ) : (
              <p>{para2}</p>
            ))}
          <div className="grid grid-cols-2 gap-6 pt-6">
            {stats.map((s, i) => (
              <div
                key={`${s.k}-${i}`}
                data-testid={`stat-${i}`}
              >
                <div className="font-display text-3xl md:text-4xl text-bone-50">
                  {s.v}
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-bone-200/50 mt-2">
                  {s.k}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
